import { SteppedDialogFormProps, useSteppedDialogFormControls } from '@mews-commander/form';
import { createZodFormResolver, lt } from '@mews/framework';
import {
    PortfolioMultipleEnterpriseProductAddParameters,
    useRepositorySuspenseQuery,
} from '@mews-commander/data-access';
import { Spinner, Stack } from '@optimus-web/core';
import { WithEnterpriseIds } from '../../../enterpriseRateManagement/types';
import { useAddPortfolioEnterpriseProducts } from '../../queries/ProductForm';
import { handleImagePayload } from '../../utils/handleImagePayload';
import { useEnterprisesProductContext } from '../../context/EnterprisesProductContext';
import { Option } from '../../hooks/useOptionTabs';
import {
    accountingConfigurationSchema,
    CentralProductConfigureFormValues,
    ConfigureProductFormStep,
    detailsConfigurationSchema,
    pricingSchema,
    servicesConfigurationSchema,
    taxesSchema,
} from '../forms/schemas/configureProductFormSchema';
import { useGetDataWithDefaultCurrency } from '../../hooks/useGetDataWithDefaultCurrency';
import { DetailsForm } from './steps/DetailsForm';
import { TaxationSinglePropertyForm } from './steps/taxation/TaxationSinglePropertyForm';
import { TaxationMultiplePropertiesForm } from './steps/taxation/TaxationMultiplePropertiesForm';
import { PricingSinglePropertyForm } from './steps/pricing/PricingSinglePropertyForm';
import { PricingMultiplePropertiesForm } from './steps/pricing/PricingMultiplePropertiesForm';
import { AccountingForm } from './steps/AccountingForm';
import { ServicesForm } from './steps/services/ServicesForm';
import { configureProductSteps } from './utils';

const ConfigureProductServiceForm = () => {
    const {
        state: { enterprises, selectedEnterpriseIds },
    } = useEnterprisesProductContext();

    const {
        data: { serviceKeys, servicesList },
    } = useRepositorySuspenseQuery(
        'portfolioServices',
        'getIntersectedKeys',
        {
            enterpriseIds: selectedEnterpriseIds,
        },
        {
            select: (data) => ({
                serviceKeys: data.serviceKeys,
                servicesList: data.serviceKeys.map(({ key }) => ({ label: key, value: key })) as Option[],
            }),
        }
    );

    return <ServicesForm servicesList={servicesList} serviceKeys={serviceKeys} enterprises={enterprises} />;
};

export const ConfigureProductFormContent = ({
    enterpriseIds,
    ...props
}: SteppedDialogFormProps<ConfigureProductFormStep> & WithEnterpriseIds) => {
    const {
        isFetching,
        state: { enterprises, centralProduct },
    } = useEnterprisesProductContext();

    const addPortfolioEnterpriseProducts = useAddPortfolioEnterpriseProducts();
    const taxEnvironmentCodes = enterprises.map(({ taxEnvironmentCode }) => taxEnvironmentCode);

    const { data, isFetching: isFetchingDataWithDefaultCurrency } = useGetDataWithDefaultCurrency();

    const defaultValues = {
        centralProductId: centralProduct.id,
        ...(enterpriseIds.length > 1
            ? {
                  pricing: {
                      ...enterprises.reduce(
                          (acc, { defaultCurrencyCode, id }) => ({
                              ...acc,
                              [id]: {
                                  currencyCode: defaultCurrencyCode,
                              },
                          }),
                          {}
                      ),
                  },
              }
            : {}),
    } as CentralProductConfigureFormValues;

    const { renderFormContent } = useSteppedDialogFormControls<
        ConfigureProductFormStep,
        CentralProductConfigureFormValues
    >({
        ...props,
        values: defaultValues,
        steps: configureProductSteps,
        stepResolvers: {
            details: createZodFormResolver(detailsConfigurationSchema),
            taxes: createZodFormResolver(taxesSchema),
            pricing: createZodFormResolver(pricingSchema),
            accounting: createZodFormResolver(accountingConfigurationSchema),
            services: createZodFormResolver(servicesConfigurationSchema),
        },
        onSubmit: async (values) => {
            const { centralProductId, image, languages, pricing, services, taxEnvironmentCode, ...rest } = values;

            const servicesMapped = Object.entries(services || {}).map(
                ([
                    key,
                    {
                        charging,
                        consumptionMoment,
                        options,
                        promotions,
                        fixedPriceAdjustments,
                        percentagePriceAdjustments,
                    },
                ]) => ({
                    serviceKey: key,
                    charging,
                    consumptionMoment: consumptionMoment || null,
                    options: options ?? [],
                    promotions: promotions ?? [],
                    fixedPriceAdjustments:
                        Object.entries(fixedPriceAdjustments || {}).map(([k, v]) => ({
                            propertyId: k,
                            ...v,
                        })) ?? null,
                    percentagePriceAdjustments:
                        Object.entries(percentagePriceAdjustments || {}).map(([k, { amountPart, multiplier }]) => ({
                            propertyId: k,
                            amountPart,
                            multiplier: multiplier / 100,
                        })) ?? null,
                })
            );

            const imageValue = await handleImagePayload(image);

            const { externalNames, descriptions } = Object.entries(languages).reduce(
                (acc, [locale, languageValues]) => {
                    if (languageValues) {
                        acc.externalNames[locale] = languageValues.externalName ?? '';
                        acc.descriptions[locale] = languageValues.description ?? '';
                    }
                    return acc;
                },
                {
                    externalNames: {} as Record<string, string>,
                    descriptions: {} as Record<string, string>,
                }
            );

            const payload: PortfolioMultipleEnterpriseProductAddParameters = {
                centralProductId,
                enterpriseProducts: Object.entries(pricing).map(([key, value]) => ({
                    propertyId: key,
                    basePrice: value,
                })),
                sharedData: {
                    details: {
                        descriptions,
                        externalNames,
                        image: imageValue,
                    },
                    taxes: Object.entries(taxEnvironmentCode).map(([key, { taxRate }]) => ({
                        taxEnvironmentCode: key,
                        taxRates: Object.values(taxRate),
                    })),
                    accounting: rest,
                    serviceProducts: servicesMapped,
                },
            };

            // mutateAsync to show errors and not close dialog
            await addPortfolioEnterpriseProducts.mutateAsync(payload);
        },
    });

    if (isFetching) {
        return (
            <Stack alignment="center" itemAlignment="center" fill>
                <Spinner />
            </Stack>
        );
    }

    return renderFormContent((step) => {
        switch (step) {
            case ConfigureProductFormStep.Details:
                return <DetailsForm />;
            case ConfigureProductFormStep.Taxes:
                return enterpriseIds.length === 1 ? (
                    <TaxationSinglePropertyForm />
                ) : (
                    <TaxationMultiplePropertiesForm
                        taxEnvironmentCodes={taxEnvironmentCodes}
                        title={lt('ConfigureProductSubHeadingTax')}
                        description={lt('ConfigureProductTaxBody')}
                    />
                );
            case ConfigureProductFormStep.Pricing:
                return enterpriseIds.length === 1 ? (
                    <PricingSinglePropertyForm />
                ) : (
                    <PricingMultiplePropertiesForm tableData={data} isFetching={isFetchingDataWithDefaultCurrency} />
                );
            case ConfigureProductFormStep.Accounting:
                return <AccountingForm />;
            case ConfigureProductFormStep.Services:
                return <ConfigureProductServiceForm />;
        }
    });
};
